import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { UtilService } from 'src/app/services/util.service';
import { AuthService } from 'src/app/services/auth.service';
import { ScrollStatus } from 'smooth-scrollbar/interfaces';
import { RequestService } from 'src/app/services/request.service';
import { User } from 'src/app/models/user';
import { LocalStorageEnum } from 'src/app/models/enums/local-storage.enum';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { LANG } from 'src/app/models/enums/lang.enum';
import SmoothScrollbar from 'smooth-scrollbar';
import { HeaderTitle } from 'src/app/models/header-title';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { SingleSignOnService } from 'src/app/services/single-sign-on.service';
import { environment } from 'src/environments/environment';
import { EXTENDED_ROUTE_PMS } from 'src/app/models/enums/route-permission';
import { fadeAnimation } from 'src/app/animations/fade-animation';

@Component({
	selector: 'app-navigation',
	templateUrl: './navigation.component.html',
	styleUrls: ['./navigation.component.scss'],
	animations: [fadeAnimation],
})
export class NavigationComponent implements OnInit, OnDestroy {
	isTablet!: boolean;
	isHandset!: boolean;

	NSSF_AUTH_URL: string = environment.NSSF_AUTH_URL;
	IS_ENABLE_OAUTH: boolean = environment.IS_ENABLE_OAUTH;
	NSSF_ADMIN_AUTH_CLIENT_ID: string = environment.NSSF_ADMIN_AUTH_CLIENT_ID;
	IS_SYSTEM_TESTING: boolean = environment.IS_SYSTEM_TESTING;
	version: string = environment.VERSION;

	isContentScrolled!: boolean;
	scrollToTop: boolean = false;

	displayMenuItems: MenuItem[] = [];

	isScrolled!: boolean;
	isMenuIcon: boolean = false;
	menuState: 'open' | 'close' = 'close';
	user!: User;
	lang: string = '';
	permissions: string[] = [];
	title: HeaderTitle | null = null;
	showActionButton: boolean = false;
	readonly LANG = LANG;

	private _destroyed = new Subject<void>();

	constructor(
		private singleSignOnService: SingleSignOnService,
		private breakpointObserver: BreakpointObserver,
		private translate: TranslateService,
		public util: UtilService,
		private authService: AuthService,
		private requestService: RequestService,
		private localStorageService: LocalStorageService,
		private _cd: ChangeDetectorRef,
		private _router: Router
	) {
		this.lang = this.translate.currentLang as LANG;

		let value: any = this.localStorageService.decryptSpecialCharacter(
			LocalStorageEnum.user
		);
		this.user = JSON.parse(value ? value : '');

		this.util.showActionButton
			.pipe(takeUntil(this._destroyed))
			.subscribe((res) => {
				this.showActionButton = !!res;
			});

		this.permissions = localStorageService.getArray(
			LocalStorageEnum.permissions
		);

		// Auto logout
		if (this.IS_ENABLE_OAUTH && !this.singleSignOnService.isLoggedIn) {
			this.authService.logout().subscribe((res: any) => {
				if (res.status === 1) {
					this.authService.logOutEventEmitter.emit(true);
					this.requestService.onSignOut();
				}
			});
		}
	}

	private _updateActive(root: string) {
		const active = this.displayMenuItems.find((menu) => menu.root === root);
		if (Boolean(active)) {
			if (!active!.expanded) {
				active!.expanded = true;
				this._cd.detectChanges();
			}
		}
	}

	ngOnInit(): void {
		this.getMenuItems();
		this._updateActive(this._router.url.slice(1).split('/').shift()!);
		this._router.events.pipe(takeUntil(this._destroyed)).subscribe((ev) => {
			if (ev instanceof NavigationEnd) {
				const root = ev.url.slice(1).split('/').shift()!;
				this._updateActive(root);
			}
		});

		this.util.headerTitle.subscribe((title) => {
			this.title = title;
		});

		this.breakpointObserver
			.observe(Breakpoints.Handset)
			.pipe(takeUntil(this._destroyed))
			.subscribe((response) => {
				this.isHandset = response.matches;
				this.isMenuIcon = response.matches;
			});

		this.breakpointObserver
			.observe(Breakpoints.TabletPortrait)
			.pipe(takeUntil(this._destroyed))
			.subscribe((response) => {
				this.isTablet = response.matches;
				this.isMenuIcon = response.matches;
			});

		this.translate.onLangChange
			.pipe(takeUntil(this._destroyed))
			.subscribe((event: LangChangeEvent) => {
				this.lang = event.lang as LANG;
			});
	}

	private traverseMenuItems(menuItems: MenuItem[]) {
		return menuItems.filter((item) => {
			if (item.children) {
				item.children = this.traverseMenuItems(item.children);
			}

			// if (!item.roles || !this.user?.role?.code) {
			//   return true;
			// }

			// return item.roles.includes(this.user.role.code);
			return true; //show all menus
		});
	}

	getMenuItems() {
		// Template for checking permission (not available yet)
		// ------
		// isNoPermission: [...EXTENDED_ROUTE_PMS.REQUEST_FORM, ...EXTENDED_ROUTE_PMS.REQUEST_LIST].filter(p => this.permissions?.includes(p)).length === 0,

		const menuItems: MenuItem[] = [
			{
				title: 'menu.dashboard',
				routerLink: '/dashboard',
				icon: 'dashboard',
				isNoPermission:
					EXTENDED_ROUTE_PMS.DASHBOARD.filter((p) =>
						this.permissions?.includes(p)
					).length === 0,
				roles: [],
			},
			{
				title: 'menu.online_claim',
				routerLink: '/receive-online-claim',
				icon: 'cast_for_education',
				isNoPermission: EXTENDED_ROUTE_PMS.BENEFIT_DOC_CLAIM_REQ.filter((p) => this.permissions?.includes(p)).length === 0,
				roles: [],
			},
			{
				title: 'menu.receive_claim_req.title',
				routerLink: '/receive-claim-request',
				icon: 'contact_emergency',
				isNoPermission: EXTENDED_ROUTE_PMS.BENEFIT_DOC_CLAIM_REQ.filter((p) => this.permissions?.includes(p)).length === 0,
				roles: [],
			},
			{
				title: 'menu.contribute_list',
				routerLink: '/member-contribute-list',
				icon: 'savings',
				roles: [],
			},

			{
				title: 'menu.history_require',
				icon: 'report',
				roles: [],
				root: 'history-require',
				children: [
					{
						title: 'menu.benefit_receipt_history',
						routerLink: '/history-require/benefit-receipt-history',
						icon: 'history',
						roles: [],
					},
					{
						title: 'menu.health_cal.emergency_sick_leave',
						routerLink: '/history-require/emergency-sick-leave-history',
						icon: 'history',
						roles: [],
					},
				],
			},
			{
				title: 'menu.injury_request.injury_request',
				icon: 'report',
				isNoPermission:
					[
						...EXTENDED_ROUTE_PMS.PRIMARY_WI_REGISTRATION,
						...EXTENDED_ROUTE_PMS.PRIMARY_WI_VALIDATION,
						...EXTENDED_ROUTE_PMS.MERGE_SEPARATE_VICTIM,
					].filter((p) => this.permissions?.includes(p)).length === 0,
				roles: [],
				root: 'accident-request',
				children: [
					{
						title: 'menu.injury_request.injury_Report',
						routerLink: '/accident-request/register',
						icon: 'app_registration',
						isNoPermission:
							EXTENDED_ROUTE_PMS.PRIMARY_WI_REGISTRATION.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.injury_request.injury_list',
						routerLink: '/accident-request/request',
						icon: 'fact_check',
						isNoPermission:
							EXTENDED_ROUTE_PMS.PRIMARY_WI_VALIDATION.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.injury_request.injury_list_Control',
						routerLink: '/accident-request/injury-report',
						icon: 'sync_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.MERGE_SEPARATE_VICTIM.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					// {
					//   title: 'menu.injury_request.injury_Risk_Report',
					//   routerLink: '/accident-request/injury-report-Control',
					//   icon: 'list_alt',
					//   roles: [],
					// },
					// {
					//   title: 'menu.injury_request.Worker_noreport',
					//   routerLink: '/accident-request/worker-non-report',
					//   icon: 'person',
					//   roles: [],
					// },
				],
			},
			{
				title: 'menu.injury_request.risk_assessment',
				icon: 'report',
				isNoPermission:
					[
						...EXTENDED_ROUTE_PMS.PRIMARY_WI_REGISTRATION,
						...EXTENDED_ROUTE_PMS.PRIMARY_WI_VALIDATION,
						...EXTENDED_ROUTE_PMS.MERGE_SEPARATE_VICTIM,
						...EXTENDED_ROUTE_PMS.INSPECTION_RESULT,
					].filter((p) => this.permissions?.includes(p)).length === 0,
				roles: [],
				root: 'accident-risk',
				children: [
					{
						title: 'menu.inspection.inspection_result',
						routerLink: '/accident-risk/inspection-result',
						icon: 'fact_check',
						isNoPermission:
							EXTENDED_ROUTE_PMS.INSPECTION_RESULT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.inspection.List_of_victim_contacts',
						routerLink: '/accident-risk/list-of-victim-contacts',
						icon: 'fact_check',
						isNoPermission:
							EXTENDED_ROUTE_PMS.INSPECTION_RESULT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},

				],
			},
			// {
			// 	title: 'menu.inspection.title',
			// 	icon: 'diversity_3',
			// 	root: 'inspection',
			// 	isNoPermission:
			// 		[
			// 			...EXTENDED_ROUTE_PMS.INSPECTION_REQUEST,
			// 			...EXTENDED_ROUTE_PMS.INSPECTION_RESULT,
			// 		].filter((p) => this.permissions?.includes(p)).length === 0,
			// 	roles: [],
			// 	children: [
			// 		{
			// 			title: 'menu.inspection.inspection_request',
			// 			routerLink: '/inspection/inspection-request',
			// 			icon: 'app_registration',
			// 			isNoPermission:
			// 				EXTENDED_ROUTE_PMS.INSPECTION_REQUEST.filter((p) =>
			// 					this.permissions?.includes(p)
			// 				).length === 0,
			// 			roles: [],
			// 		},
			// 		{
			// 			title: 'menu.inspection.inspection_result',
			// 			routerLink: '/inspection/inspection-result',
			// 			icon: 'fact_check',
			// 			isNoPermission:
			// 				EXTENDED_ROUTE_PMS.INSPECTION_RESULT.filter((p) =>
			// 					this.permissions?.includes(p)
			// 				).length === 0,
			// 			roles: [],
			// 		},
			// 	],
			// },
			// {
			//   title: 'menu.temp_wi_victim.title',
			//   routerLink: '/temp-wi-victim',
			//   icon: 'report',
			//   root: 'temp-wi-victim',
			//   isNoPermission:
			//     EXTENDED_ROUTE_PMS.TEMP_WI_VICTIM.filter((p) =>
			//       this.permissions?.includes(p)
			//     ).length === 0,
			//   roles: [],
			// },
			{
				title: 'menu.retreatment.retreatment_injury',
				routerLink: '/retreatment-injury',
				icon: 'personal_injury',
				root: 'retreatment-injury',
				isNoPermission:
					EXTENDED_ROUTE_PMS.RETREATMENT_REQUEST.filter((p) =>
						this.permissions?.includes(p)
					).length === 0,
				roles: [],
			},
			{
				title: 'menu.claim_report.receipt',
				icon: 'diversity_3',
				root: 'claim-report',
				isNoPermission:
					[
						...EXTENDED_ROUTE_PMS.CLAIM_TEMP_REQUEST,
						...EXTENDED_ROUTE_PMS.CLAIM_REQUEST,
					].filter((p) => this.permissions?.includes(p)).length === 0,
				roles: [],
				children: [
					{
						title: 'menu.claim_report.temporary',
						routerLink: '/claim-report/temporaryclaimreport',
						icon: 'fact_check',
						isNoPermission:
							EXTENDED_ROUTE_PMS.CLAIM_TEMP_REQUEST.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.claim_report.receive',
						routerLink: '/claim-report/receiveclaimreport',
						icon: 'content_paste_search',
						isNoPermission:
							EXTENDED_ROUTE_PMS.CLAIM_REQUEST.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.care_ben.doctor_or_calculator_request',
						routerLink: '/claim-report/calculator-doctor',
						icon: 'request_quote',
						isNoPermission:
							EXTENDED_ROUTE_PMS.CLAIM_REQUEST.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					// {
					//   title: 'menu.claim_report.order',
					//   routerLink: '/claim-report/orderclaimreport',
					//   icon: 'list_alt',
					//   roles: [],
					// },
					// {
					//   title: 'menu.claim_report.submit',
					//   routerLink: '/claim-report/submitclaimreport',
					//   icon: 'list_alt',
					//   roles: [],
					// },
				],
			},
			{
				title: 'menu.svv_inspection.title',
				icon: 'diversity_3',
				root: 'svv-inspection',
				isNoPermission:
					[
						...EXTENDED_ROUTE_PMS.SVV_INSPECTION_REQUEST,
						...EXTENDED_ROUTE_PMS.SVV_INSPECTION_RESULT,
					].filter((p) => this.permissions?.includes(p)).length === 0,
				roles: [],
				children: [
					// {
					// 	title: 'menu.svv_inspection.inspection_request',
					// 	routerLink: '/svv-inspection/svv-inspection-request',
					// 	icon: 'app_registration',
					// 	isNoPermission:
					// 		EXTENDED_ROUTE_PMS.SVV_INSPECTION_REQUEST.filter((p) =>
					// 			this.permissions?.includes(p)
					// 		).length === 0,
					// 	roles: [],
					// },
					{
						title: 'menu.svv_inspection.inspection_result',
						routerLink: '/svv-inspection/svv-inspection-result',
						icon: 'fact_check',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SVV_INSPECTION_RESULT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
				],
			},
			{
				title: 'menu.victim_ben.victim_ben_cal',
				routerLink: '/victim-benifit',
				icon: 'point_of_sale',
				root: 'victim-benifit',
				isNoPermission:
					EXTENDED_ROUTE_PMS.DAILY_INCOME_RECORD.filter((p) =>
						this.permissions?.includes(p)
					).length === 0,
				roles: [],
			},
			{
				title: 'menu.ben_cal.ben_host',
				icon: 'receipt_long',
				root: 'claim-request-hc',
				isNoPermission:
					[
						...EXTENDED_ROUTE_PMS.MEDICAL_CLAIM_REQUEST_HC,
						...EXTENDED_ROUTE_PMS.TEMPORARY_CLAIM_REQUEST_HC
					].filter((p) => this.permissions?.includes(p)).length === 0,
				roles: [],
				children: [
					{
						title: 'menu.ben_cal.ben_treatment_doc',
						routerLink: '/claim-request-hc/health-care',
						icon: 'list_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.MEDICAL_CLAIM_REQUEST_HC.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.ben_cal.ben_temporary_doc',
						routerLink: '/claim-request-hc/temporary',
						icon: 'list_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.TEMPORARY_CLAIM_REQUEST_HC.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
				],
			},
			{
				title: 'menu.doctor_document.title',
				routerLink: '/doctor-document',
				icon: 'snippet_folder',
				root: 'doctor-document',
				isNoPermission:
					EXTENDED_ROUTE_PMS.DOCTOR_DOCUMENT_REQUEST.filter((p) =>
						this.permissions?.includes(p)
					).length === 0,
				roles: [],
			},
			// {
			// 	title: 'menu.permanent_request.title',
			// 	routerLink: '/permanent-request',
			// 	icon: 'how_to_reg',
			// 	root: 'permanent-request',
			// 	isNoPermission: EXTENDED_ROUTE_PMS.PERMANENT_REQUEST_NOTE.filter((p) => this.permissions?.includes(p)).length === 0,
			// 	roles: [],
			// },
			{
				title: 'menu.perm_loss_ass_req',
				routerLink: '/perm-loss-ass-req',
				icon: 'accessible_forward',
				root: 'perm-loss-ass-req',
				isNoPermission: EXTENDED_ROUTE_PMS.PERMANENT_REQUEST_VALUATION.filter((p) => this.permissions?.includes(p)).length === 0,
				roles: [],
				// children: [
				// 	{
				// 		title: 'menu.new_perm_valuate',
				// 		routerLink: '/perm-loss-ass-req/new-case',
				// 		icon: 'bookmark_stacks',
				// 		// isNoPermission: EXTENDED_ROUTE_PMS.MEDICAL_CLAIM_REQUEST_HC.filter((p) => this.permissions?.includes(p)).length === 0,
				// 		roles: [],
				// 	},
				// 	{
				// 		title: 'menu.old_perm_valuate',
				// 		routerLink: '/perm-loss-ass-req/old-case',
				// 		icon: 'list_alt',
				// 		// isNoPermission: EXTENDED_ROUTE_PMS.MEDICAL_CLAIM_REQUEST_HC.filter((p) => this.permissions?.includes(p)).length === 0,
				// 		roles: [],
				// 	},
				// ],
			},
			{
				title: 'menu.ben_cal.ben_cal_job',
				icon: 'receipt_long',
				roles: [],
				root: 'claim-request', //benifit-calculate
				isNoPermission:
					[
						...EXTENDED_ROUTE_PMS.MEDICAL_CLAIM_REQUEST,
						...EXTENDED_ROUTE_PMS.TEMPORARY_CLAIM_REQUEST,
						...EXTENDED_ROUTE_PMS.PERMANENT_CLAIM_REQUEST,
						...EXTENDED_ROUTE_PMS.ALLOWANCE_CLAIM_REQUEST,
						...EXTENDED_ROUTE_PMS.SURVIVAL_CLAIM_REQUEST,
						...EXTENDED_ROUTE_PMS.CALCULATE_CLAIM_REQUEST,
					].filter((p) => this.permissions?.includes(p)).length === 0,
				children: [
					{
						title: 'menu.ben_cal.ben_treatment',
						routerLink: '/claim-request/health-care',
						icon: 'list_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.MEDICAL_CLAIM_REQUEST.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.ben_cal.ben_temporary',
						routerLink: '/claim-request/temporary',
						icon: 'list_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.TEMPORARY_CLAIM_REQUEST.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.ben_cal.ben_permanent',
						routerLink: '/claim-request/permanent',
						icon: 'list_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.PERMANENT_CLAIM_REQUEST.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.ben_cal.ben_cremental',
						routerLink: '/claim-request/allowance',
						icon: 'list_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.ALLOWANCE_CLAIM_REQUEST.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.ben_cal.ben_survival',
						routerLink: '/claim-request/survival',
						icon: 'list_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SURVIVAL_CLAIM_REQUEST.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.ben_cal.ben_cal_job_title',
						routerLink: '/claim-request/calculation',
						icon: 'list_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.CALCULATE_CLAIM_REQUEST.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},

					// {
					//   title: 'menu.ben_cal.ben_treatment',
					//   routerLink: '/benifit-calculate/treatmentbenefit',
					//   icon: 'list_alt',
					//   roles: [],
					// },
					// {
					//   title: 'menu.ben_cal.ben_temporary',
					//   routerLink: '/benifit-calculate/temporarybenefit',
					//   icon: 'list_alt',
					//   roles: [],
					// },
					// {
					//   title: 'menu.ben_cal.ben_permanent',
					//   routerLink: '/benifit-calculate/permanentbenefit',
					//   icon: 'list_alt',
					//   roles: [],
					// },
					// {
					//   title: 'menu.ben_cal.ben_cremental',
					//   routerLink: '/benifit-calculate/cremationbenefit',
					//   icon: 'list_alt',
					//   roles: [],
					// },
					// {
					//   title: 'menu.ben_cal.ben_survival',
					//   routerLink: '/benifit-calculate/deadbenefit',
					//   icon: 'list_alt',
					//   roles: [],
					// },
					// {
					//   title: 'menu.ben_cal.ben_request',
					//   routerLink: '/benifit-calculate/benefitdemand',
					//   icon: 'list_alt',
					//   roles: [],
					// },
				],
			},
			{
				title: 'menu.print.title',
				routerLink: '/print',
				icon: 'print_connect',
				root: 'print',
				isNoPermission:
					[
						...EXTENDED_ROUTE_PMS.PRINT_INFO,
						...EXTENDED_ROUTE_PMS.REQUEST_INFO,
					].filter((p) => this.permissions?.includes(p)).length === 0,
				roles: [],
				children: [
					{
						title: 'menu.print.print_info',
						routerLink: '/print/print-info',
						icon: 'print_connect',
						isNoPermission:
							EXTENDED_ROUTE_PMS.PRINT_INFO.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					// {
					// 	title: 'menu.print.request_info',
					// 	routerLink: '/print/request-info',
					// 	icon: 'app_registration',
					// 	isNoPermission:
					// 		EXTENDED_ROUTE_PMS.REQUEST_INFO.filter((p) =>
					// 			this.permissions?.includes(p)
					// 		).length === 0,
					// 	roles: [],
					// },
				],
			},
			{
				title: 'menu.ben_con.ben_confirm_req',
				icon: 'receipt_long',
				roles: [],
				root: 'benefit-approval',
				isNoPermission:
					[
						...EXTENDED_ROUTE_PMS.BENEFIT_APPROVAL_PRINT,
						...EXTENDED_ROUTE_PMS.BENEFIT_APPROVAL_CONFIRM,
						// ...EXTENDED_ROUTE_PMS.BENEFIT_APPROVAL_YEARLY_REPORT,
					].filter((p) => this.permissions?.includes(p)).length === 0,
				children: [
					{
						title: 'menu.ben_con.print_ben_con',
						routerLink: '/benefit-approval/benefitprint',
						icon: 'list_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.BENEFIT_APPROVAL_PRINT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					// {
					// 	title: 'menu.ben_con.ben_con_transfer',
					// 	routerLink: '/benefit-approval/benefitapproval',
					// 	icon: 'list_alt',
					// 	isNoPermission:
					// 		EXTENDED_ROUTE_PMS.BENEFIT_APPROVAL_CONFIRM.filter((p) =>
					// 			this.permissions?.includes(p)
					// 		).length === 0,
					// 	roles: [],
					// },
					// {
					//   title: 'menu.ben_con.branch_ben_order',
					//   routerLink: '/benefit-approval/branchorder',
					//   icon: 'list_alt',
					//   roles: [],
					// },
					// {
					//   title: 'menu.ben_con.ben_receive_list',
					//   routerLink: '/benefit-approval/receiver',
					//   icon: 'list_alt',
					//   isNoPermission:
					//     EXTENDED_ROUTE_PMS.BENEFIT_APPROVAL_RECEIVER.filter((p) =>
					//       this.permissions?.includes(p)
					//     ).length === 0,
					//   roles: [],
					// },
					// {
					//   title: 'menu.ben_con.ben_yearly_report',
					//   routerLink: '/benefit-approval/yearlyreport',
					//   icon: 'list_alt',
					//   isNoPermission:
					//     EXTENDED_ROUTE_PMS.BENEFIT_APPROVAL_YEARLY_REPORT.filter((p) =>
					//       this.permissions?.includes(p)
					//     ).length === 0,
					//   roles: [],
					// },
				],
			},
			// {
			// 	title: 'menu.re_svv_inspection.title',
			// 	routerLink: '/re-svv-inspection',
			// 	icon: 'diversity_3',
			// 	root: 're-svv-inspection',
			// 	isNoPermission: [
			// 		// ...EXTENDED_ROUTE_PMS.RE_SVV_REQUEST,
			// 		...EXTENDED_ROUTE_PMS.RE_SVV_RESULT,
			// 	].filter((p) => this.permissions?.includes(p)).length === 0,
			// 	children: [
			// 		// {
			// 		//   title:'menu.re_svv_inspection.inspection_request',
			// 		//   routerLink:'/re-svv-inspection/re-svv-inspection-request',
			// 		//   icon:'app_registration',
			// 		//   isNoPermission:EXTENDED_ROUTE_PMS.RE_SVV_REQUEST.filter((p)=>this.permissions?.includes(p)).length === 0
			// 		// },
			// 		{
			// 			title: 'menu.re_svv_inspection.inspection_result',
			// 			routerLink: '/re-svv-inspection/re-svv-inspection-result',
			// 			icon: 'fact_check',
			// 			isNoPermission: EXTENDED_ROUTE_PMS.RE_SVV_RESULT.filter((p) => this.permissions?.includes(p)).length === 0
			// 		}
			// 	]
			// },
			{
				title: 'menu.monthly_pension_payment.title',
				routerLink: '/monthly-pension-payment',
				icon: 'accessible',
				root: 'monthly-pension-payment',
				isNoPermission: [
					...EXTENDED_ROUTE_PMS.PERMANENT_MONTHLY_PAYMENT,
					...EXTENDED_ROUTE_PMS.PERMANENT_INFLATION,
					...EXTENDED_ROUTE_PMS.PERMANENT_REEVALUATE,
					...EXTENDED_ROUTE_PMS.PERMANENT_PAYMENT_DEDUCT,
				].filter((p) => this.permissions?.includes(p)).length === 0,
				children: [
					{
						title: 'menu.mpp.perm_mpp',
						routerLink: '/monthly-pension-payment/perm-mpp',
						icon: 'list_alt',
						isNoPermission: EXTENDED_ROUTE_PMS.PERMANENT_MONTHLY_PAYMENT.filter((p) => this.permissions?.includes(p)).length === 0,
					},
					{
						title: 'menu.pension_payment_annual_inflat.annaul_inflate',
						routerLink: '/monthly-pension-payment/annual-inflat',
						icon: 'payment_arrow_down',
						isNoPermission: EXTENDED_ROUTE_PMS.PERMANENT_INFLATION.filter((p) => this.permissions?.includes(p)).length === 0,
					},
					{
						title: 'menu.svv_monthly.payment_reevaluate',
						routerLink: '/monthly-pension-payment/payment-reevaluate',
						icon: 'price_change',
						isNoPermission: EXTENDED_ROUTE_PMS.PERMANENT_REEVALUATE.filter((p) => this.permissions?.includes(p)).length === 0,
					},
					{
						title: 'menu.svv_monthly.payment_allow_amount',
						routerLink: '/monthly-pension-payment/payment-allow-amount',
						icon: 'account_balance_wallet',
						isNoPermission: EXTENDED_ROUTE_PMS.PERMANENT_PAYMENT_DEDUCT.filter((p) => this.permissions?.includes(p)).length === 0,
					},
				],
			},
			{
				title: 'menu.svv_monthly.title',
				routerLink: '/svv-monthly-payment/svv-payment',
				icon: 'church',
				root: 'svv-monthly-payment',
				isNoPermission: [
					...EXTENDED_ROUTE_PMS.SURVIVAL_MONTHLY_PAYMENT,
					...EXTENDED_ROUTE_PMS.SURVIVAL_REEVALUATE,
					...EXTENDED_ROUTE_PMS.SURVIVAL_PAYMENT_DEDUCT,
				].filter((p) => this.permissions?.includes(p)).length === 0,
				children: [
					{
						title: 'menu.svv_monthly.svv_claimer',
						routerLink: '/svv-monthly-payment/svv-payment',
						icon: 'contact_emergency',
						isNoPermission: EXTENDED_ROUTE_PMS.SURVIVAL_MONTHLY_PAYMENT.filter((p) => this.permissions?.includes(p)).length === 0,
					},
					{
						title: 'menu.svv_monthly.payment_reevaluate',
						routerLink: '/svv-monthly-payment/payment-reevaluate',
						icon: 'price_change',
						isNoPermission: EXTENDED_ROUTE_PMS.SURVIVAL_REEVALUATE.filter((p) => this.permissions?.includes(p)).length === 0,
					},
					{
						title: 'menu.svv_monthly.payment_allow_amount',
						routerLink: '/svv-monthly-payment/payment-allow-amount',
						icon: 'account_balance_wallet',
						isNoPermission: EXTENDED_ROUTE_PMS.SURVIVAL_PAYMENT_DEDUCT.filter((p) => this.permissions?.includes(p)).length === 0,
					},
				],
			},
			{
				title: "payment_ben_note",
				routerLink: "/payment-note",
				icon: "note_alt",
				root: "payment-note",
				isNoPermission: [
					...EXTENDED_ROUTE_PMS.MONTHLY_PAYMENT_NOTE,
					...EXTENDED_ROUTE_PMS.MONTHLY_PAYMENT_NOTE_CONFIRM,
				].filter((p) => this.permissions?.includes(p)).length === 0,
				children: [
					{
						title: 'ben_note',
						routerLink: '/payment-note/ben-note',
						icon: 'print_connect',
						isNoPermission: EXTENDED_ROUTE_PMS.MONTHLY_PAYMENT_NOTE.filter((p) => this.permissions?.includes(p)).length === 0,
					},
					{
						title: 'ben_note_confirm',
						routerLink: '/payment-note/ben-note-confirm',
						icon: 'app_registration',
						isNoPermission: EXTENDED_ROUTE_PMS.MONTHLY_PAYMENT_NOTE_CONFIRM.filter((p) => this.permissions?.includes(p)).length === 0,
					},
				]
			},
			// {
			//   title: 'menu.ben_order.ben_order',
			//   routerLink: '/benefit-order',
			//   icon: 'note_alt',
			//   root: 'benefit-order',
			//   roles: [],
			// },
			{
				title: 'menu.non_ben.non_ben_job',
				icon: 'receipt_long',
				roles: [],
				root: 'benefit-non-occupate',
				isNoPermission:
					[
						...EXTENDED_ROUTE_PMS.BENEFIT_NON_OCCUPATION_VICTIM,
						...EXTENDED_ROUTE_PMS.BENEFIT_NON_OCCUPATION_MEDICAL,
						...EXTENDED_ROUTE_PMS.BENEFIT_NON_OCCUPATION_NO_REFUND,
					].filter((p) => this.permissions?.includes(p)).length === 0,
				children: [
					{
						title: 'menu.non_ben.non_ben_victim',
						routerLink: '/benefit-non-occupate/nonoccupatevictims',
						icon: 'post_add',
						isNoPermission:
							EXTENDED_ROUTE_PMS.BENEFIT_NON_OCCUPATION_VICTIM.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.non_ben.med_ben',
						routerLink: '/benefit-non-occupate/medicalbenefit',
						icon: 'post_add',
						isNoPermission:
							EXTENDED_ROUTE_PMS.BENEFIT_NON_OCCUPATION_MEDICAL.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.non_ben.ben_non_req',
						routerLink: '/benefit-non-occupate/nonrefund',
						icon: 'post_add',
						isNoPermission:
							EXTENDED_ROUTE_PMS.BENEFIT_NON_OCCUPATION_NO_REFUND.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
				],
			},
			{
				title: 'menu.ship_ben.ship_cal',
				icon: 'local_shipping',
				roles: [],
				root: 'shipping-benefit',
				isNoPermission:
					[
						...EXTENDED_ROUTE_PMS.BENEFIT_SHIPPING_FEE,
						...EXTENDED_ROUTE_PMS.BENEFIT_SHIPPING,
						...EXTENDED_ROUTE_PMS.BENEFIT_SHIPPING_DECISION,
					].filter((p) => this.permissions?.includes(p)).length === 0,
				children: [
					{
						title: 'menu.ship_ben.ship_ben_info',
						routerLink: '/shipping-benefit/shippingbenefit',
						icon: 'paid',
						isNoPermission:
							EXTENDED_ROUTE_PMS.BENEFIT_SHIPPING_FEE.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.ship_ben.ship_detail',
						routerLink: '/shipping-benefit/shippinglist',
						icon: 'preview',
						isNoPermission:
							EXTENDED_ROUTE_PMS.BENEFIT_SHIPPING.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.ship_ben.approval',
						routerLink: '/shipping-benefit/shippinggive',
						icon: 'preview',
						isNoPermission:
							EXTENDED_ROUTE_PMS.BENEFIT_SHIPPING_DECISION.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
				],
			},
			// {
			//   title: 'menu.work_data.work_act_data',
			//   routerLink: '/work-activity',
			//   icon: 'browser_updated',
			//   root: 'work-activity',
			//   isNoPermission:
			//     EXTENDED_ROUTE_PMS.WORK_ACTIVITY_REQUEST.filter((p) =>
			//       this.permissions?.includes(p)
			//     ).length === 0,
			//   roles: [],
			// },
			// {
			//   title: 'menu.ben_scan.ben_maternity',
			//   routerLink: '/maternity-doc',
			//   icon: 'document_scanner',
			//   root: 'maternity-doc',
			//   roles: [],
			// },
			{
				title: 'menu.care_ben.ben_health',
				icon: 'receipt_long',
				roles: [],
				root: 'healthcare-benefit',
				isNoPermission:
					[
						...EXTENDED_ROUTE_PMS.BENEFIT_HEALTH_CARE_RECEIVER,
						...EXTENDED_ROUTE_PMS.BENEFIT_HEALTH_CARE_TRANSFER_DOC,
						...EXTENDED_ROUTE_PMS.BENEFIT_HEALTH_CARE_RECEIVED,
					].filter((p) => this.permissions?.includes(p)).length === 0,
				children: [
					// {
					// 	title: 'button.consumption_sync',
					// 	routerLink: '/healthcare-benefit/hspis-sync',
					// 	icon: 'move_to_inbox',
					// 	isNoPermission:
					// 		EXTENDED_ROUTE_PMS.BENEFIT_HEALTH_CARE_RECEIVER.filter((p) =>
					// 			this.permissions?.includes(p)
					// 		).length === 0,
					// 	roles: [],
					// },
					{
						title: 'menu.care_ben.ben_health_receive',
						routerLink: '/healthcare-benefit/healthreceivebenefit',
						icon: 'diversity_1',
						isNoPermission:
							EXTENDED_ROUTE_PMS.BENEFIT_HEALTH_CARE_RECEIVER.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					// {
					// 	title: 'menu.care_ben.doctor_or_calculator_request',
					// 	routerLink: '/healthcare-benefit/doctor-caculator-request',
					// 	icon: 'list_alt',
					// 	isNoPermission:
					// 		EXTENDED_ROUTE_PMS.BENEFIT_HEALTH_CARE_RECEIVER.filter((p) =>
					// 			this.permissions?.includes(p)
					// 		).length === 0,
					// 	roles: [],
					// },
					// {
					//   title: 'menu.care_ben.ben_health_transfer',
					//   routerLink: '/healthcare-benefit/healthtransferdoc',
					//   icon: 'list_alt',
					//   isNoPermission:
					//     EXTENDED_ROUTE_PMS.BENEFIT_HEALTH_CARE_TRANSFER_DOC.filter((p) =>
					//       this.permissions?.includes(p)
					//     ).length === 0,
					//   roles: [],
					// },
					// {
					//   title: 'menu.care_ben.branch_ben_order',
					//   routerLink: '/healthcare-benefit/healthbranch',
					//   icon: 'list_alt',
					//   roles: [],
					// },
					// {
					//   title: 'menu.care_ben.ben_receive_list',
					//   routerLink: '/healthcare-benefit/receivedbenefit',
					//   icon: 'list_alt',
					//   isNoPermission:
					//     EXTENDED_ROUTE_PMS.BENEFIT_HEALTH_CARE_RECEIVED.filter((p) =>
					//       this.permissions?.includes(p)
					//     ).length === 0,
					//   roles: [],
					// },
				],
			},
			{
				title: 'menu.health_hospital.title',
				icon: 'receipt_long',
				roles: [],
				root: 'hospital-support',
				isNoPermission:
					[
						...EXTENDED_ROUTE_PMS.EMERGENCY_CLAIM_REQUEST_HC,
						...EXTENDED_ROUTE_PMS.SICK_LEAVE_CLAIM_REQUEST_HC,
						...EXTENDED_ROUTE_PMS.HC_DOCTOR_REPORT,
					].filter((p) => this.permissions?.includes(p)).length === 0,
				children: [
					{
						title: 'menu.health_cal.emergency_doc',
						routerLink: '/hospital-support/emergency',
						icon: 'list_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.EMERGENCY_CLAIM_REQUEST_HC.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.health_cal.sick_leave_doc',
						routerLink: '/hospital-support/daily-allowance',
						icon: 'list_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SICK_LEAVE_CLAIM_REQUEST_HC.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.health_cal.doctor_report',
						routerLink: '/hospital-support/doctor-report',
						icon: 'list_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.HC_DOCTOR_REPORT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
				],
			},
			{
				title: 'menu.health_cal.health_cal',
				icon: 'receipt_long',
				roles: [],
				root: 'healthcare-calculation',
				isNoPermission:
					[
						...EXTENDED_ROUTE_PMS.BENEFIT_HEALTH_CARE_RECEIVER,
						...EXTENDED_ROUTE_PMS.BENEFIT_HEALTH_CARE_TRANSFER_DOC,
						...EXTENDED_ROUTE_PMS.BENEFIT_HEALTH_CARE_RECEIVED,
					].filter((p) => this.permissions?.includes(p)).length === 0,
				children: [
					// {
					//   title: 'menu.health_cal.health_care',
					//   routerLink: '/healthcare-calculation/health-care',
					//   icon: 'list_alt',
					//   isNoPermission:
					//     EXTENDED_ROUTE_PMS.MEDICAL_CLAIM_REQUEST.filter((p) =>
					//       this.permissions?.includes(p)
					//     ).length === 0,
					//   roles: [],
					// },
					{
						title: 'menu.health_cal.emergency',
						routerLink: '/healthcare-calculation/emergency',
						icon: 'list_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.HC_EMERGENCY_CLAIM_REQUEST.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.health_cal.maternity',
						routerLink: '/healthcare-calculation/maternity',
						icon: 'list_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.HC_MATERNITY_CLAIM_REQUEST.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.health_cal.sick_leave',
						routerLink: '/healthcare-calculation/sick-leave',
						icon: 'list_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.HC_SICK_LEAVE_CLAIM_REQUEST.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					// {
					//   title: 'menu.health_cal.transportation',
					//   routerLink: '/healthcare-calculation/transportion',
					//   icon: 'list_alt',
					//   isNoPermission:
					//     EXTENDED_ROUTE_PMS.HC_TRANSPORT_CLAIM_REQUEST.filter((p) =>
					//       this.permissions?.includes(p)
					//     ).length === 0,
					//   roles: [],
					// },
					{
						title: 'menu.health_cal.funeral',
						routerLink: '/healthcare-calculation/funeral',
						icon: 'list_alt',
						isNoPermission:
							EXTENDED_ROUTE_PMS.HC_FUNERAL_CLAIM_REQUEST.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
				],
			},
			{
				title: 'menu.health_note.title',
				icon: 'receipt_long',
				root: 'healthcare-note',
				isNoPermission:
					[...EXTENDED_ROUTE_PMS.NOTE, ...EXTENDED_ROUTE_PMS.PROVINCE_BRANCH_NOTE, ...EXTENDED_ROUTE_PMS.APPROVAL].filter(
						(p) => this.permissions?.includes(p)
					).length === 0,
				roles: [],
				children: [
					{
						title: 'menu.health_note.note',
						routerLink: '/healthcare-note/note',
						icon: 'print_connect',
						isNoPermission:
							EXTENDED_ROUTE_PMS.NOTE.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.health_note.province_branch_note',
						routerLink: '/healthcare-note/province-branch-note',
						icon: 'account_tree',
						isNoPermission:
							EXTENDED_ROUTE_PMS.PROVINCE_BRANCH_NOTE.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.health_note.daily_note',
						routerLink: '/healthcare-note/daily-hc-note-overall',
						icon: 'diversity_1',
						isNoPermission:
							EXTENDED_ROUTE_PMS.DAILY_HC_NOTE_OVERALL.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.health_note.request',
						routerLink: '/healthcare-note/request',
						icon: 'app_registration',
						isNoPermission:
							EXTENDED_ROUTE_PMS.APPROVAL.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.health_note.adjustment',
						routerLink: '/healthcare-note/adjustment',
						icon: 'price_change',
						isNoPermission:
							EXTENDED_ROUTE_PMS.NOTE_ADJUSTMENT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
				],
			},

			// {
			//   title: 'menu.health_doc.health_ben_doc',
			//   routerLink: '/healthcare-benefit-doc',
			//   icon: 'note_alt',
			//   root: 'healthcare-benefit-doc',
			//   roles: [],
			// },

			// {
			//   title: 'menu.ben_list.ben_list_app',
			//   routerLink: '/benefit-table',
			//   icon: 'table_rows_narrow',
			//   root: 'benefit-table',
			//   isNoPermission:
			//     EXTENDED_ROUTE_PMS.BENEFIT_TABLE.filter((p) =>
			//       this.permissions?.includes(p)
			//     ).length === 0,
			//   roles: [],
			// },
			{
				title: "menu.overall_note_confirm.title",
				routerLink: "/overall-note-confirm",
				icon: "note_alt",
				root: "overall-note-confirm",
				isNoPermission:
					[
						...EXTENDED_ROUTE_PMS.WI_NOTE_CONFIRM_OVERALL,
						...EXTENDED_ROUTE_PMS.HC_NOTE_CONFIRM_OVERALL,
					].filter((p) => this.permissions?.includes(p)).length === 0,
				roles: [],
				children: [
					{
						title: 'menu.overall_note_confirm.wi_note_confirm',
						routerLink: '/overall-note-confirm/wi-note-confirm-overall',
						icon: 'print_connect',
						isNoPermission: EXTENDED_ROUTE_PMS.WI_NOTE_CONFIRM_OVERALL.filter((p) => this.permissions?.includes(p)).length === 0,
						roles: [],
					},
					{
						title: 'menu.overall_note_confirm.hc_note_confirm',
						routerLink: '/overall-note-confirm/hc-note-confirm-overall',
						icon: 'app_registration',
						isNoPermission: EXTENDED_ROUTE_PMS.HC_NOTE_CONFIRM_OVERALL.filter((p) => this.permissions?.includes(p)).length === 0,
						roles: [],
					},
				]
			},
			{
				title: "menu.overall_branch_note.title",
				routerLink: "/overall-branch-note",
				icon: "note_alt",
				root: "overall-branch-note",
				isNoPermission:
					[
						...EXTENDED_ROUTE_PMS.BRANCH_NOTE_OVERALL,
						...EXTENDED_ROUTE_PMS.BRANCH_NOTE_RECEIVE,
						...EXTENDED_ROUTE_PMS.BRANCH_NOTE_RESULT
					].filter((p) => this.permissions?.includes(p)).length === 0,
				roles: [],
				children: [
					// {
					// 	title: 'menu.overall_branch_note.request',
					// 	routerLink: '/overall-branch-note/request',
					// 	icon: 'print_connect',
					// 	isNoPermission: EXTENDED_ROUTE_PMS.BRANCH_NOTE_OVERALL.filter((p) => this.permissions?.includes(p)).length === 0,
					// 	roles: [],
					// },
					// {
					// 	title: 'menu.overall_branch_note.receive_request',
					// 	routerLink: '/overall-branch-note/receive-request',
					// 	icon: 'app_registration',
					// 	isNoPermission: EXTENDED_ROUTE_PMS.BRANCH_NOTE_RECEIVE.filter((p) => this.permissions?.includes(p)).length === 0,
					// 	roles: [],
					// },
					{
						title: 'menu.overall_branch_note.receive_result',
						routerLink: '/overall-branch-note/branch-note-result',
						icon: 'quiz',
						isNoPermission: EXTENDED_ROUTE_PMS.BRANCH_NOTE_RESULT.filter((p) => this.permissions?.includes(p)).length === 0,
						roles: [],
					},
				]
			},
			{
				title: 'menu.role_transfer',
				routerLink: '/role-transfer',
				icon: 'connect_without_contact',
				root: 'role-transfer',
				isNoPermission:
					EXTENDED_ROUTE_PMS.ROLE_TRANSFER.filter((p) =>
						this.permissions?.includes(p)
					).length === 0,
				roles: [],
			},
			{
				title: 'menu.tracking_system',
				routerLink: '/tracking-system',
				icon: 'edit_notifications',
				root: 'tracking-system',
				isNoPermission:
					EXTENDED_ROUTE_PMS.SYSTEM_TRACKING.filter((p) =>
						this.permissions?.includes(p)
					).length === 0,
				roles: [],
			},
			{
				title: 'menu.user_admin',
				routerLink: '/user-admin',
				icon: 'manage_accounts',
				root: 'user-admin',
				isNoPermission:
					EXTENDED_ROUTE_PMS.NSSF_USER.filter((p) =>
						this.permissions?.includes(p)
					).length === 0,
				roles: [],
			},
			{
				title: 'menu.setting',
				icon: 'settings',
				roles: [],
				isNoPermission:
					EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
						this.permissions?.includes(p)
					).length === 0,
				root: 'setting',
				expanded: false,
				children: [
					{
						title: 'menu.department',
						routerLink: 'setting/department',
						icon: 'meeting_room',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.role',
						routerLink: 'setting/role',
						icon: 'work',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},

					{
						title: 'setting.disability_type',
						routerLink: 'setting/disablity-type',
						icon: 'accessible',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},

					{
						title: 'setting.relationship',
						routerLink: 'setting/relationship',
						icon: 'supervisor_account',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					// {
					//   title: 'setting.hospital.hospital',
					//   routerLink: 'setting/hospital',
					//   icon: 'local_hospital',
					//   isNoPermission: EXTENDED_ROUTE_PMS.SETTING.filter(p => this.permissions?.includes(p)).length === 0,
					//   roles: [],
					// },
					// {
					//   title: 'setting.medicine.group',
					//   routerLink: 'setting/medicine-group',
					//   icon: 'local_hospital',
					//   isNoPermission:
					//     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
					//       this.permissions?.includes(p)
					//     ).length === 0,
					//   roles: [],
					// },
					// {
					//   title: 'setting.medicine.info',
					//   routerLink: 'setting/medicine-info',
					//   icon: 'healing',
					//   isNoPermission:
					//     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
					//       this.permissions?.includes(p)
					//     ).length === 0,
					//   roles: [],
					// },
					{
						title: 'setting.accident.group',
						routerLink: 'setting/accident-group',
						icon: 'car_crash',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'setting.account.type',
						routerLink: 'setting/account-type',
						icon: 'switch_account',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'setting.bank',
						routerLink: 'setting/bank',
						icon: 'account_balance',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					// {
					// 	title: 'setting.benefit.group',
					// 	routerLink: 'setting/benefit-group',
					// 	icon: 'redeem',
					// 	isNoPermission:
					// 		EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
					// 			this.permissions?.includes(p)
					// 		).length === 0,
					// 	roles: [],
					// },
					// {
					// 	title: 'setting.category.visit',
					// 	routerLink: 'setting/category-visit',
					// 	icon: 'emoji_transportation',
					// 	isNoPermission:
					// 		EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
					// 			this.permissions?.includes(p)
					// 		).length === 0,
					// 	roles: [],
					// },
					{
						title: 'setting.visit_ref_type.title',
						routerLink: 'setting/visit-ref-type',
						icon: 'emoji_transportation',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					// {
					// 	title: 'setting.type.visit',
					// 	routerLink: 'setting/visit-type',
					// 	icon: 'emoji_transportation',
					// 	isNoPermission:
					// 		EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
					// 			this.permissions?.includes(p)
					// 		).length === 0,
					// 	roles: [],
					// },
					{
						title: 'menu.emergency.diagnosis_type',
						routerLink: 'setting/diagnosis-type',
						icon: 'medication',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'menu.care_ben.diagnosis',
						routerLink: 'setting/diagnosis',
						icon: 'medical_services',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},

					{
						title: 'setting.environment.work',
						routerLink: 'setting/environment-work',
						icon: 'stream',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'setting.health.status',
						routerLink: 'setting/health-status',
						icon: 'medication_liquid',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					// {
					// 	title: 'setting.history_survivors_member_inv_status',
					// 	routerLink: 'setting/history-survivors-member-inv-status',
					// 	icon: 'group',
					// 	isNoPermission:
					// 		EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
					// 			this.permissions?.includes(p)
					// 		).length === 0,
					// 	roles: [],
					// },
					// {
					// 	title: 'setting.hospital_payment.group',
					// 	routerLink: 'setting/hospital-payment-group',
					// 	icon: 'payments',
					// 	isNoPermission:
					// 		EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
					// 			this.permissions?.includes(p)
					// 		).length === 0,
					// 	roles: [],
					// },
					{
						title: 'setting.injury.group',
						routerLink: 'setting/injury-group',
						icon: 'personal_injury',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'setting.material.group',
						routerLink: 'setting/material-group',
						icon: 'home_repair_service',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					// {
					// 	title: 'setting.investigation_survivor_reason',
					// 	routerLink: 'setting/investigation-survivor-reason',
					// 	icon: 'saved_search',
					// 	isNoPermission:
					// 		EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
					// 			this.permissions?.includes(p)
					// 		).length === 0,
					// 	roles: [],
					// },
					// {
					//   title: 'setting.hospital.hospital',
					//   routerLink: 'setting/hospital',
					//   icon: 'domain_add',
					//   isNoPermission:
					//     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
					//       this.permissions?.includes(p)
					//     ).length === 0,
					//   roles: [],
					// },
					// {
					//   title: 'setting.hospital_type',
					//   routerLink: 'setting/hospital-type',
					//   icon: 'local_hospital',
					//   isNoPermission:
					//     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
					//       this.permissions?.includes(p)
					//     ).length === 0,
					//   roles: [],
					// },
					// {
					//   title: 'setting.medical_hospital_reason',
					//   routerLink: 'setting/medical-hospital-reason',
					//   icon: 'local_hospital',
					//   isNoPermission:
					//     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
					//       this.permissions?.includes(p)
					//     ).length === 0,
					//   roles: [],
					// },
					// {
					//   title: 'setting.hospital_level',
					//   routerLink: 'setting/hospital-level',
					//   icon: 'local_hospital',
					//   isNoPermission:
					//     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
					//       this.permissions?.includes(p)
					//     ).length === 0,
					//   roles: [],
					// },
					// {
					//   title: 'setting.hospital_package_level',
					//   routerLink: 'setting/hospital-package-level',
					//   icon: 'medical_services',
					//   isNoPermission:
					//     EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
					//       this.permissions?.includes(p)
					//     ).length === 0,
					//   roles: [],
					// },
					{
						title: 'setting.claim_case',
						routerLink: 'setting/hc-claim-case',
						icon: 'content_paste_search',
						isNoPermission:
							EXTENDED_ROUTE_PMS.SETTING.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
						roles: [],
					},
					{
						title: 'setting.benefit_doc_type',
						routerLink: 'setting/benefit-doc-type',
						icon: 'attach_email',
						// isNoPermission:
						//   EXTENDED_ROUTE_PMS.READ_HC_CLAIM_EXPANSE_REPORT.filter((p) =>
						//     this.permissions?.includes(p)
						//   ).length === 0,
					},
					{
						title: 'setting.inflation_rate',
						routerLink: 'setting/inflation-rate',
						icon: 'percent_discount'
					},
				],
			},
			{
				title: 'menu.work_injury_report',
				icon: 'report',
				root: 'report',
				isNoPermission: [
					...EXTENDED_ROUTE_PMS.READ_WI_CLAIM_REQ_REPORT,
					...EXTENDED_ROUTE_PMS.READ_WI_CLAIM_AMOUNT_REPORT,
					...EXTENDED_ROUTE_PMS.READ_WI_CLAIMANT_REPORT,
					...EXTENDED_ROUTE_PMS.READ_WI_CLAIM_BY_ENT_ADDR_REPORT,
					...EXTENDED_ROUTE_PMS.READ_WI_CLAIM_APPROVAL_REPORT,
					...EXTENDED_ROUTE_PMS.READ_WI_CLAIM_EXPANSE_REPORT,
				].filter(p => this.permissions?.includes(p)).length === 0,
				roles: [],
				children: [
					// {
					//   title: 'menu.occupational_risk_expense',
					//   routerLink: '/report/wi/claim-wi',
					//   icon: 'corporate_fare',
					//   isNoPermission:
					//     EXTENDED_ROUTE_PMS.READ_WI_CLAIM_REQ_REPORT.filter((p) =>
					//       this.permissions?.includes(p)
					//     ).length === 0,
					// },
					{
						title: 'menu.injury_request.print_open_account',
						routerLink: '/report/wi/open-account',
						icon: 'corporate_fare',
						isNoPermission:
							EXTENDED_ROUTE_PMS.READ_WI_CLAIM_AMOUNT_REPORT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
					},
					{
						title: 'menu.provision_occupational_risk_assessment',
						routerLink: '/report/wi/officer-wi',
						icon: 'corporate_fare',
						isNoPermission:
							EXTENDED_ROUTE_PMS.READ_WI_CLAIM_AMOUNT_REPORT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
					},
					{
						title: 'menu.list_beneficiaries',
						routerLink: '/report/wi/list-wi-member',
						icon: 'corporate_fare',
						isNoPermission:
							EXTENDED_ROUTE_PMS.READ_WI_CLAIMANT_REPORT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
					},
					{
						title: 'menu.wi_claimants',
						routerLink: '/report/wi/benefit-receive-claim-num',
						icon: 'corporate_fare',
						isNoPermission:
							EXTENDED_ROUTE_PMS.READ_WI_CLAIM_AMOUNT_REPORT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
					},
					{
						title: 'menu.occupational_risk_expense',
						routerLink: '/report/wi/num-decision-occupation-risk-report',
						icon: 'corporate_fare',
						isNoPermission:
							EXTENDED_ROUTE_PMS.READ_WI_CLAIM_AMOUNT_REPORT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
					},
					{
						title: 'menu.claim_wi',
						routerLink: '/report/wi/claim-wi-report',
						icon: 'corporate_fare',
						isNoPermission:
							EXTENDED_ROUTE_PMS.READ_WI_CLAIM_BY_ENT_ADDR_REPORT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
					},
					{
						title: 'menu.provision_claim_wi',
						routerLink: '/report/wi/provision_claim-wi-report',
						icon: 'corporate_fare',
						isNoPermission:
							EXTENDED_ROUTE_PMS.READ_WI_CLAIM_APPROVAL_REPORT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
					},
					{
						title: 'menu.expend_claim_wi',
						routerLink: '/report/wi/payment-claim-wi-report',
						icon: 'corporate_fare',
						isNoPermission:
							EXTENDED_ROUTE_PMS.READ_WI_CLAIM_EXPANSE_REPORT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
					},
					{
						title: 'menu.decision_report',
						routerLink: '/report/wi/offer-check-by-province-report',
						icon: 'corporate_fare',
						isNoPermission:
							EXTENDED_ROUTE_PMS.READ_WI_CLAIM_EXPANSE_REPORT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
					},
					{
						title: 'menu.work_injury.treatment_by_branch',
						routerLink: '/report/wi/treatment-by-branch',
						icon: 'personal_injury',
						isNoPermission: EXTENDED_ROUTE_PMS.READ_WI_CLAIM_EXPANSE_REPORT.filter((p) => this.permissions?.includes(p)).length === 0,
					},
					{
						title: 'menu.work_injury.itemized_medical_foreigner_report',
						routerLink: '/report/wi/medical-foreigner-report',
						icon: 'article_person',
						isNoPermission: EXTENDED_ROUTE_PMS.READ_WI_CLAIM_EXPANSE_REPORT.filter((p) => this.permissions?.includes(p)).length === 0,
					},
					{
						title: 'menu.work_injury.cremation_report',
						routerLink: '/report/wi/cremation-report',
						icon: 'article_person',
						isNoPermission: EXTENDED_ROUTE_PMS.READ_WI_CLAIM_EXPANSE_REPORT.filter((p) => this.permissions?.includes(p)).length === 0,
					},
					{
						title: 'menu.work_injury.wi_working_result',
						routerLink: '/report/wi/working-result-report',
						icon: 'article_person',
						isNoPermission: EXTENDED_ROUTE_PMS.READ_WI_CLAIM_EXPANSE_REPORT.filter((p) => this.permissions?.includes(p)).length === 0,
					},
					{
						title: 'menu.work_injury.pension_report',
						routerLink: '/report/wi/pension-report',
						icon: 'article_person',
						isNoPermission: EXTENDED_ROUTE_PMS.READ_WI_CLAIM_EXPANSE_REPORT.filter((p) => this.permissions?.includes(p)).length === 0,
					},
				],
			},
			{
				title: 'menu.hc_report',
				icon: 'report',
				root: 'report-hc',
				isNoPermission: [
					...EXTENDED_ROUTE_PMS.READ_HC_CLAIM_REQ_REPORT,
					...EXTENDED_ROUTE_PMS.READ_HC_CLAIM_AMOUNT_REPORT,
					...EXTENDED_ROUTE_PMS.READ_HC_CLAIM_BY_ENT_ADDR_REPORT,
					...EXTENDED_ROUTE_PMS.READ_HC_CLAIM_APPROVAL_REPORT,
					...EXTENDED_ROUTE_PMS.READ_HC_CLAIM_EXPANSE_REPORT,
				].filter(p => this.permissions?.includes(p)).length === 0,
				roles: [],
				children: [
					{
						title: 'menu.injury_request.print_open_account',
						routerLink: '/report-hc/open-account',
						icon: 'corporate_fare',
						isNoPermission:
							EXTENDED_ROUTE_PMS.READ_WI_CLAIM_AMOUNT_REPORT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
					},
					// {
					// 	title: 'menu.health_care_claim',
					// 	routerLink: '/report-hc/hc-claim',
					// 	icon: 'corporate_fare',
					// 	isNoPermission:
					// 		EXTENDED_ROUTE_PMS.READ_HC_CLAIM_REQ_REPORT.filter((p) =>list-hc-member
					// 			this.permissions?.includes(p)
					// 		).length === 0,
					// },
					// {
					//   title: 'menu.summary_request',
					//   routerLink: '/report-hc/summary',
					//   icon: 'corporate_fare',
					//   isNoPermission:
					//     EXTENDED_ROUTE_PMS.READ_HC_CLAIM_AMOUNT_REPORT.filter((p) =>
					//       this.permissions?.includes(p)
					//     ).length === 0,
					// },
					// {
					// 	title: 'menu.list_beneficiaries',
					// 	routerLink: '/report-hc/list-hc-member',
					// 	icon: 'corporate_fare',
					// 	isNoPermission:
					// 		EXTENDED_ROUTE_PMS.READ_WI_CLAIMANT_REPORT.filter((p) =>
					// 			this.permissions?.includes(p)
					// 		).length === 0,
					// },
					{
						title: 'menu.report_consumption_info',
						routerLink: '/report-hc/consumption-info',
						icon: 'corporate_fare',
						isNoPermission:
							EXTENDED_ROUTE_PMS.READ_WI_CLAIMANT_REPORT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
					},
					{
						title: 'menu.injury_request.title_claim_hc_num',
						routerLink: '/report-hc/claim-hc-number',
						icon: 'corporate_fare',
						isNoPermission:
							EXTENDED_ROUTE_PMS.READ_WI_CLAIMANT_REPORT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
					},
					{
						title: 'menu.claim_hc_pay',
						routerLink: '/report-hc/claim-hc-payments',
						icon: 'corporate_fare',
						isNoPermission:
							EXTENDED_ROUTE_PMS.READ_WI_CLAIMANT_REPORT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
					},
					{
						title: 'menu.report_request_provinces',
						routerLink: '/report-hc/claim',
						icon: 'corporate_fare',
						isNoPermission:
							EXTENDED_ROUTE_PMS.READ_HC_CLAIM_BY_ENT_ADDR_REPORT.filter((p) =>
								this.permissions?.includes(p)
							).length === 0,
					},
					// {
					// 	title: 'menu.report_payment_hc',
					// 	routerLink: '/report-hc/payment',
					// 	icon: 'corporate_fare',
					// 	isNoPermission:
					// 		EXTENDED_ROUTE_PMS.READ_HC_CLAIM_APPROVAL_REPORT.filter((p) =>
					// 			this.permissions?.includes(p)
					// 		).length === 0,
					// },
					// {
					// 	title: 'menu.summary_report_nationality',
					// 	routerLink: '/report-hc/summary-report-nationality-hc',
					// 	icon: 'corporate_fare',
					// 	// isNoPermission:
					// 	//   EXTENDED_ROUTE_PMS.READ_HC_CLAIM_BY_ENT_ADDR_REPORT.filter((p) =>
					// 	//     this.permissions?.includes(p)
					// 	//   ).length === 0,
					// },

					// {
					// 	title: 'menu.report_detailed_nationality',
					// 	routerLink: '/report-hc/report-detail-nationality-hc',
					// 	icon: 'corporate_fare',
					// 	isNoPermission:
					// 	  EXTENDED_ROUTE_PMS.READ_HC_CLAIM_BY_ENT_ADDR_REPORT.filter((p) =>
					// 	    this.permissions?.includes(p)
					// 	  ).length === 0,
					// },

					{
						title: 'menu.report_detailed_nationality',
						routerLink: '/report-hc/report-detail-nationality-hc-summary',
						icon: 'corporate_fare',
						// isNoPermission:
						//   EXTENDED_ROUTE_PMS.READ_HC_CLAIM_BY_ENT_ADDR_REPORT.filter((p) =>
						//     this.permissions?.includes(p)
						//   ).length === 0,
					},

					{
						title: 'menu.sector_hc',
						routerLink: '/report-hc/health-care-sector',
						icon: 'corporate_fare',
						// isNoPermission:
						//   EXTENDED_ROUTE_PMS.READ_HC_CLAIM_BY_ENT_ADDR_REPORT.filter((p) =>
						//     this.permissions?.includes(p)
						//   ).length === 0,
					},
					{
						title: 'menu.hc_nssf',
						routerLink: '/report-hc/hc-claim-table-by-month-per-year',
						icon: 'corporate_fare',
						// isNoPermission:
						//   EXTENDED_ROUTE_PMS.READ_HC_CLAIM_BY_ENT_ADDR_REPORT.filter((p) =>
						//     this.permissions?.includes(p)
						//   ).length === 0,
					},


					{
						title: 'menu.health_care_claim_enterprise',
						routerLink: '/report-hc/hc-claim-enterprise',
						icon: 'corporate_fare',
						// isNoPermission:
						//   EXTENDED_ROUTE_PMS.READ_HC_CLAIM_EXPANSE_REPORT.filter((p) =>
						//     this.permissions?.includes(p)
						//   ).length === 0,
					},

					// {
					// 	title: 'menu.health_care_daily_expense_summary',
					// 	routerLink: '/report-hc/hc-daily-expense-summary',
					// 	icon: 'corporate_fare',
					// 	// isNoPermission:
					// 	//   EXTENDED_ROUTE_PMS.READ_HC_CLAIM_EXPANSE_REPORT.filter((p) =>
					// 	//     this.permissions?.includes(p)
					// 	//   ).length === 0,
					// },
					{
						title: 'menu.health_care_branch_claimant_count',
						routerLink: '/report-hc/hc-branch-claimant-count',
						icon: 'corporate_fare',
						// isNoPermission:
						//   EXTENDED_ROUTE_PMS.READ_HC_CLAIM_EXPANSE_REPORT.filter((p) =>
						//     this.permissions?.includes(p)
						//   ).length === 0,
					},
					{
						title: 'menu.health_care_claim_by_type_and_branch',
						routerLink: '/report-hc/hc-claim-type-and-branch',
						icon: 'corporate_fare',
					},
					{
						title: 'menu.claim_submission_schedule_medical_doctor',
						routerLink: '/report-hc/claim-submission-schedule-medical-doctor',
						icon: 'corporate_fare',
					},
					// {
					// 	title: 'menu.health_care_schedule',
					// 	routerLink: '/report-hc/receiving-fund-health',
					// 	icon: 'corporate_fare',
					// 	// isNoPermission:
					// 	//   EXTENDED_ROUTE_PMS.READ_HC_CLAIM_EXPANSE_REPORT.filter((p) =>
					// 	//     this.permissions?.includes(p)
					// 	//   ).length === 0,
					// },

					{
						title: 'menu.health_care_doc_claimant_count',
						routerLink: '/report-hc/doc-sub-count',
						icon: 'corporate_fare',
					},

					{
						title: 'menu.rejected_ben_claims',
						routerLink: '/report-hc/rejected-ben-claims',
						icon: 'corporate_fare',
					},
					{
						title: 'menu.hc_daily_calculator',
						routerLink: '/report-hc/hc-daily-calculator',
						icon: 'corporate_fare',
					},
					{
						title: 'menu.hc_receiver_by_province',
						routerLink: '/report-hc/hc-receiver-by-province',
						icon: 'corporate_fare',
					}
				],
			},
			// {
			// 	title: 'menu.expense_report_accounting',
			// 	icon: 'report',
			// 	root: 'report-hc',
			// 	// isNoPermission: [
			// 	// 	...EXTENDED_ROUTE_PMS.READ_HC_CLAIM_REQ_REPORT,
			// 	// 	...EXTENDED_ROUTE_PMS.READ_HC_CLAIM_AMOUNT_REPORT,
			// 	// 	...EXTENDED_ROUTE_PMS.READ_HC_CLAIM_BY_ENT_ADDR_REPORT,
			// 	// 	...EXTENDED_ROUTE_PMS.READ_HC_CLAIM_APPROVAL_REPORT,
			// 	// 	...EXTENDED_ROUTE_PMS.READ_HC_CLAIM_EXPANSE_REPORT,
			// 	// ].filter(p => this.permissions?.includes(p)).length === 0,
			// 	roles: [],
			// 	children: [
			// 		{
			// 			title: 'menu.injury_request.injury_request',
			// 			routerLink: '/expense-report-accounting/list-name-for-work-injury',
			// 			icon: 'corporate_fare',
			// 			// isNoPermission:
			// 			// 	EXTENDED_ROUTE_PMS.READ_WI_CLAIM_AMOUNT_REPORT.filter((p) =>
			// 			// 		this.permissions?.includes(p)
			// 			// 	).length === 0,
			// 		},
			// 		{
			// 			title: 'menu.hc_report',
			// 			routerLink: '/expense-report-accounting/list-name-for-hc',
			// 			icon: 'corporate_fare',
			// 			// isNoPermission:
			// 			// 	EXTENDED_ROUTE_PMS.READ_HC_CLAIM_REQ_REPORT.filter((p) =>
			// 			// 		this.permissions?.includes(p)
			// 			// 	).length === 0,
			// 		},
			// 	],
			// },
		];
		this.displayMenuItems = this.traverseMenuItems(menuItems);
	}

	onToggleMenu(): void {
		this.isMenuIcon = !this.isMenuIcon;
		this.util.menuChange.emit(this.isMenuIcon);
	}

	onCloseMenu(): void {
		this.isMenuIcon = true;
		this.util.menuChange.emit(this.isMenuIcon);
	}

	toggleExpendMenu(menu: MenuItem): void {
		menu.expanded = !menu.expanded;
	}

	onMenuScroll(e: ScrollStatus): void {
		let scrolled = e.offset.y > 0;
		if (this.isScrolled != scrolled) {
			this.isScrolled = scrolled;
			this._cd.detectChanges();
		}
	}

	onClickMenu() {
		if (!this.isMenuIcon) {
			this.isMenuIcon = this.isHandset || this.isTablet;
		}
	}

	onLogout() {
		if (this.IS_ENABLE_OAUTH && this.singleSignOnService.isLoggedIn) {
			this.singleSignOnService.onLogout();
		} else {
			this.authService.logout().subscribe((res: any) => {
				if (res.status === 1) {
					this.authService.logOutEventEmitter.emit(true);
					this.requestService.onSignOut();
				}
			});
		}
	}

	onScroll(status: ScrollStatus): void {
		let scrolled = status.offset.y > 200;
		if (this.isContentScrolled != scrolled) {
			this.isContentScrolled = scrolled;
			this._cd.detectChanges();
		}

		this.util.navigationScroll.emit(status);
	}

	onScrollLoaded(scroll: SmoothScrollbar): void {
		this.util.navigationScrollRef = scroll;
	}

	moveTop() {
		this.util.navigationScrollRef.scrollTo(0, 0, 350);
		this.util.navigationScrollRef.setMomentum(0, 1);
	}

	ngOnDestroy(): void {
		this._destroyed.next();
		this._destroyed.complete();
	}
}

export interface MenuItem {
	title: string; // Title to be displayed
	exact?: boolean; // Highlight when active or not
	routerLink?: string; // Link to redirect when click
	children?: MenuItem[]; // Children of the menu
	roles?: string[]; // Allowed roles for the menu
	icon?: string; // Icon to be displayed
	root?: string;
	expanded?: boolean; // Show children or not
	isNoPermission?: boolean;
}
